import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import { FC, useContext, useEffect, useRef } from "react";
import { AxiosError } from "axios";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useSaveEditStructureForm from "src/Pages/Company/OrganizationStructure/api/useSaveEditStructureForm";
import useUnsavedChangesGuard from "src/Pages/Company/OrganizationStructure/utils/useUnsavedChangesGuard";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import { EditAvatar } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureSettings/EditAvatar/EditAvatar";
import { useHistory } from "react-router-dom";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import {
  EditStructureFormSchema,
  IEditFormProps,
  IFormValues,
} from "./EditStructureForm.schema";
import { styles } from "./editStructureFormStyle";

export const EditStructureForm: FC<IEditFormProps> = ({
  disableEditMode,
  structureId,
  currentAvatar,
  structureName,
}) => {
  const formMethods = useForm<IFormValues>({
    resolver: standardSchemaResolver(EditStructureFormSchema),
    defaultValues: {
      name: structureName,
      avatarBase64: currentAvatar,
      deleteAvatar: false,
    },
    mode: "all",
  });

  const { setModal, resetModal } = useContext(GlobalModalContext);
  const history = useHistory();

  const unblockRef = useRef<(() => void) | null>(null);
  const nextLocationRef = useRef<any>(null);
  const proceedNavigationRef = useRef<(() => void) | null>(null);

  const { control, handleSubmit, watch } = formMethods;

  const watchedAvatar = watch("avatarBase64");
  const deleteAvatar = watchedAvatar === null;

  const companyId = useCompanyId();
  const saveEditFormMutation = useSaveEditStructureForm(
    companyId,
    structureId,
    disableEditMode
  );

  const logStatus = useLogStatus();

  useEffect(() => {
    if (!saveEditFormMutation.error) return;
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "EditStructureForm: saveEditStructureForm failed",
      statusDetails: {
        source: "API",
        operation: "saveEditStructureForm",
        status: (saveEditFormMutation.error as AxiosError)?.response?.status,
        url: (saveEditFormMutation.error as AxiosError)?.config?.url,
      },
      errorObject:
        saveEditFormMutation.error instanceof Error
          ? saveEditFormMutation.error
          : new Error(String(saveEditFormMutation.error)),
    });
  }, [saveEditFormMutation.error, logStatus]);

  const confirmLeaving = useUnsavedChangesGuard({
    onExit: () => {
      if (proceedNavigationRef.current) proceedNavigationRef.current();
      disableEditMode();
    },
    onCancel: () => {
      proceedNavigationRef.current = null;
      nextLocationRef.current = null;
    },
    setModal,
    resetModal,
  });

  useEffect(() => {
    unblockRef.current = history.block((tx) => {
      nextLocationRef.current = tx;

      proceedNavigationRef.current = () => {
        if (unblockRef.current) unblockRef.current();
        history.push(tx.pathname);
      };

      confirmLeaving();
      return false;
    });

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a") as HTMLAnchorElement;

      if (link && link.href) {
        const currentOrigin = window.location.origin;
        const currentPath = window.location.pathname;
        const linkUrl = new URL(link.href, currentOrigin);

        const isDifferentOrigin = linkUrl.origin !== currentOrigin;
        const isDifferentPath = linkUrl.pathname !== currentPath;
        const isExternalNavigation =
          isDifferentOrigin || link.target === "_blank";

        if (
          (isExternalNavigation || isDifferentPath) &&
          !link.getAttribute("data-react-router-link")
        ) {
          e.preventDefault();
          e.stopPropagation();

          proceedNavigationRef.current = () => {
            if (link.target === "_blank") {
              window.open(link.href, "_blank");
            } else {
              window.location.href = link.href;
            }
          };

          confirmLeaving();
        }
      }
    };

    document.addEventListener("click", handleLinkClick, true);

    return () => {
      if (unblockRef.current) unblockRef.current();
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [history, confirmLeaving]);

  const onSubmit = (formData: IFormValues) => {
    const isBase64 = formData.avatarBase64?.startsWith("data:image");
    saveEditFormMutation.mutate({
      name: formData.name,
      avatarBase64: isBase64 ? formData.avatarBase64 : null,
      deleteAvatar,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <Paper
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          p: 3,
          background: "#F9F9F9",
          border: "1px solid #DDDDDD",
          boxShadow: "none",
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography sx={styles.sectionTitle}>
          {__("Nazwa struktury")}
        </Typography>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <ControlledTextField
              {...field}
              placeholder={__("Wpisz nazwę struktury")}
              fullWidth
              label={__("Nazwa struktury")}
              maxLength={128}
              normalizeSpaces
              data-testid="os__settings-name-input"
            />
          )}
        />
        <Typography sx={styles.sectionTitle}>
          {__("Avatar struktury")}:
        </Typography>
        <Controller
          name="avatarBase64"
          control={control}
          render={({ field }) => (
            <EditAvatar value={field.value} onChange={field.onChange} />
          )}
        />
        <Box sx={styles.buttonGroup}>
          <Button
            variant="outlined"
            type="button"
            size="large"
            onClick={confirmLeaving}
            data-testid="os__settings-cancel-button"
          >
            {__("Anuluj")}
          </Button>
          <Button
            variant="contained"
            size="large"
            type="submit"
            disabled={saveEditFormMutation.isPending}
            data-testid="os__settings-save-button"
          >
            {__("Zapisz zmiany")}
          </Button>
        </Box>
      </Paper>
    </FormProvider>
  );
};
